import React, { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ICustomFieldSchema } from '@/api/customField';
import { InputText } from 'primereact/inputtext';
import { ExtendedTableLowCode } from '@/components/shared/table/ExtendedTableLowCode';
import { ActionButton } from '@/components/shared/button/ActionButton';
import { CustomOptionService } from '@/api/customOptionService';
import { CustomCellProperties, TableSpec } from 'alurkerja-ui';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';

interface CustomOptionsModalProps {
  visible: boolean;
  onHide: () => void;
  data: ICustomFieldSchema;
  id: string;
  spec: TableSpec;
  customCell: (cell: CustomCellProperties) => React.ReactElement;
}

interface CustomActionCellProps {
  data: ICustomFieldSchema;
  onToggleEnable: (
    id: string,
    isEnable: boolean,
    data: ICustomFieldSchema,
  ) => void;
}

const CustomActionCell: React.FC<CustomActionCellProps> = ({
  data,
  onToggleEnable,
}) => {
  return (
    <ActionButton.Wrapper>
      {data.is_enable === true ? (
        <ActionButton.Check
          onClick={() => onToggleEnable(data.id!, false, data)}
        />
      ) : (
        <ActionButton.Close
          onClick={() => onToggleEnable(data.id!, true, data)}
        />
      )}
    </ActionButton.Wrapper>
  );
};

export const CustomOptionsModal: React.FC<CustomOptionsModalProps> = ({
  visible,
  onHide,
  id,
  spec,
  customCell,
}) => {
  const ref = useRef(null);
  const [optionName, setOptionName] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [currentAction, setCurrentAction] = useState({
    id: '',
    isEnable: false,
    data: {} as ICustomFieldSchema,
  });

  const { mutate: createMutate } = CustomOptionService.useCreate();
  const { mutate: updateMutate } = CustomOptionService.useUpdate();

  const handleToggleEnable = (
    id: string,
    isEnable: boolean,
    data: ICustomFieldSchema,
  ) => {
    setCurrentAction({ id, isEnable, data });
    setConfirmVisible(true);
  };

  const confirmToggleEnable = () => {
    const { id, isEnable, data } = currentAction;

    const payload = {
      options: data.options,
      is_enable: isEnable,
      custom_field_id: data.custom_field_id,
      id: id,
    };

    updateMutate(payload, {
      onSuccess: () => {
        setAlertMessage(
          `Option ${isEnable ? 'enabled' : 'disabled'} successfully`,
        );
        setAlertType('success');
        setOptionName('');
        setTimeout(() => {
          setAlertMessage('');
        }, 3000);
      },
      onError: () => {
        setAlertMessage(`Failed to ${isEnable ? 'enable' : 'disable'} option`);
        setAlertType('error');
        setTimeout(() => {
          setAlertMessage('');
        }, 3000);
      },
    });

    setConfirmVisible(false);
  };

  const handleAddOption = () => {
    const payload = {
      option: optionName,
      is_enable: true,
      custom_field_id: id,
    };

    createMutate(payload, {
      onSuccess: () => {
        setAlertMessage('Option added successfully');
        setAlertType('success');
        setOptionName('');
        setTimeout(() => {
          setAlertMessage('');
        }, 3000);
      },
      onError: () => {
        setAlertMessage('Failed to add option');
        setAlertType('error');
        setTimeout(() => {
          setAlertMessage('');
        }, 3000);
      },
    });
  };

  return (
    <>
      <Dialog
        visible={confirmVisible}
        style={{
          width: '30vw',
          maxWidth: '300px',
          backgroundColor: '#fefefe',
          borderRadius: '12px',
          boxShadow: '0 15px 30px rgba(0, 0, 0, 0.2)',
          padding: '20px',
        }}
        onHide={() => setConfirmVisible(false)}
        modal={true}
        draggable={false}
        resizable={false}
        closable={true}
      >
        <p>
          Are you sure you want to{' '}
          {currentAction.isEnable ? 'enable' : 'disable'} this option?
        </p>
        <div className="p-d-flex p-jc-end">
          <Button
            label="Yes"
            icon="pi pi-check"
            className="p-button-rounded p-button-success"
            onClick={confirmToggleEnable}
          />
        </div>
      </Dialog>

      <Dialog
        visible={visible}
        style={{
          width: '80vw',
          maxWidth: '1000px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
        onHide={onHide}
        className="custom-dialog"
        modal={true}
        draggable={false}
        resizable={false}
        closable={true}
      >
        <div className="p-4" style={{ lineHeight: '1.6', color: '#333' }}>
          <p
            className="mb-4 text-lg font-medium"
            style={{ fontSize: '1.2rem', fontWeight: 600 }}
          >
            Custom options
          </p>

          {alertMessage && (
            <div
              className={`alert ${
                alertType === 'success' ? 'alert-success' : 'alert-error'
              }`}
              style={{
                padding: '10px',
                borderRadius: '4px',
                marginBottom: '15px',
                color: alertType === 'success' ? '#155724' : '#721c24',
                backgroundColor:
                  alertType === 'success' ? '#d4edda' : '#f8d7da',
                border:
                  alertType === 'success'
                    ? '1px solid #c3e6cb'
                    : '1px solid #f5c6cb',
              }}
            >
              {alertMessage}
            </div>
          )}

          <div className="p-fluid">
            <div
              className="mb-3 field"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <InputText
                id="inputName"
                type="text"
                value={optionName}
                onChange={e => setOptionName(e.target.value)}
                placeholder="Enter option name"
                style={{
                  flexGrow: 1,
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  boxShadow: 'inset 0 0 5px rgba(0,0,0,0.1)',
                }}
              />
              <Button
                label="Add"
                icon="pi pi-plus"
                className="p-button-rounded p-button-primary"
                onClick={handleAddOption}
                style={{
                  backgroundColor: '#007bff',
                  borderColor: '#007bff',
                  color: '#fff',
                  marginLeft: '10px',
                  padding: '10px 16px',
                  fontSize: '0.9rem',
                }}
              />
            </div>
          </div>
          <div
            className="p-3 mb-4 surface-100 border-round shadow-2"
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              padding: '15px',
              boxShadow: 'inset 0 0 5px rgba(0,0,0,0.05)',
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            <ExtendedTableLowCode
              ref={ref}
              showCheckbox={false}
              defaultFilter={{
                custom_field_id: id,
              }}
              defaultSort="name"
              defaultSortOrder="desc"
              spec={spec}
              customButtonCreate={HideCustomCreateButton}
              customCell={customCell}
              customActionCell={data => (
                <CustomActionCell
                  data={data as ICustomFieldSchema}
                  onToggleEnable={handleToggleEnable}
                />
              )}
            />
            <p
              className="text-muted"
              style={{
                fontStyle: 'italic',
                color: '#666',
                fontSize: '0.9rem',
                marginTop: '10px',
              }}
            >
              Note: Review the information carefully before making any changes.
            </p>
          </div>
        </div>
      </Dialog>
    </>
  );
};
